// BUILD-046 Owner correction 2 (2026-10-24): what the person who issued a task reads of the work done on it, in Assigned by Me.
//
// For a task the requester may read in full the server sends (GET /work/issued, and GET /work/{id} to its requester) the latest progress note WITH its author and the activity date it names, and every RECORDED
// change of the Task due date. This module draws them:
//   - InsightLines: the labelled summary - "Latest progress (Julie, activity Oct 20): ...", "Next action: ...", "Next follow-up date: Oct 25 (missed)". Comfortable shows it under the title; Compact reaches the same
//     lines through the row's small "Details" toggle. The three are distinct: a description is never labelled as progress, and the recorded time (when the note was saved) is its own line, apart from the
//     activity date (which may be backdated).
//   - DueChangedButton / DueChangesPanel / DueChangedInline: a small persistent "Due changed" indicator; opening it lists each recorded change - previous date, new date, reason, who, and the recorded time.
//     It is a record of what was written, not an "unread" mark: nothing here claims a change is new, because no per-person reviewed state exists. A legacy task whose history holds no change shows nothing.
// Presentation only: what to show is the server's; the overdue classification still uses the CURRENT due date elsewhere.
import { useEffect, useRef, useState } from 'react';
import type { IssuedDueChange, IssuedMember, IssuedProgress } from './issuedRequestsModel';
import { NEXT_ACTION_LABEL, NEXT_FOLLOW_UP_DATE_LABEL } from './dateLabels';
import { formatDueDate } from './taskRowModel';
import { hasInsightLines, recordedAtText } from './requesterInsightModel';
import { followUpState } from './updateTaskModel';
import { PersonName } from './PersonName';
import { usePhoenixDay } from './usePhoenixDay';

const dateOf = (value: string): string => formatDueDate(value) ?? value;
export { hasInsightLines, recordedAtText };

export interface InsightLinesProps {
  progress: IssuedProgress | null;
  nextAction: string;
  followUpDate: string;
  /** The expanded form: the whole note (wrapped) and its recorded time. Default: one line, cut with an ellipsis, the whole note as its hover text. */
  full?: boolean;
  /** One-line form only: told whether the progress line is cut off by the width of the window, so the row can offer "Details" (a hover title does not exist on a touch screen). */
  onClipped?: (clipped: boolean) => void;
}

export function InsightLines({ progress, nextAction, followUpDate, full = false, onClipped }: InsightLinesProps) {
  const today = usePhoenixDay();
  const follow = formatDueDate(followUpDate);
  const state = follow === null ? 'none' : followUpState(followUpDate, today);
  const lineRef = useRef<HTMLParagraphElement>(null);
  const noteText = progress === null ? '' : progress.text;
  useEffect(() => {
    if (full || onClipped === undefined) return undefined;
    const el = lineRef.current;
    if (el === null) {
      onClipped(false);
      return undefined;
    }
    const measure = () => onClipped(el.scrollWidth > el.clientWidth + 1);
    measure();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [full, onClipped, noteText]);
  if (!hasInsightLines(progress, nextAction, followUpDate)) return null;
  const recorded = progress === null ? '' : recordedAtText(progress.recordedAt);
  return (
    <div className="space-y-0.5" data-testid="requester-insight">
      {progress !== null && (
        <div data-testid="insight-progress">
          <p ref={lineRef} className={full ? 'break-words' : 'truncate'} title={full ? undefined : progress.text}>
            <span className="font-medium text-gray-700">Latest progress</span>
            <span className="text-gray-600">
              {' ('}
              {progress.actorName !== '' ? <PersonName person={progress.actorName} focusable={false} /> : 'author not recorded'}
              {progress.activityDate !== '' ? `, activity ${dateOf(progress.activityDate)}` : ''}
              {'):'}
            </span>{' '}
            {progress.text}
          </p>
          {full && recorded !== '' && (
            <p className="text-gray-600" data-testid="insight-recorded">
              Recorded {recorded}
            </p>
          )}
        </div>
      )}
      {(nextAction.trim() !== '' || follow !== null) && (
        <p className="flex flex-wrap gap-x-4" data-testid="insight-next">
          {nextAction.trim() !== '' && (
            <span>
              <span className="font-medium text-gray-700">{NEXT_ACTION_LABEL}:</span> {nextAction}
            </span>
          )}
          {follow !== null && (
            <span>
              <span className="font-medium text-gray-700">{NEXT_FOLLOW_UP_DATE_LABEL}:</span> {follow}
              {state === 'missed' && (
                <span className="ml-1 font-medium text-amber-800" data-testid="insight-follow-missed">
                  (missed)
                </span>
              )}
              {state === 'today' && <span className="ml-1 font-medium text-amber-800">(today)</span>}
            </span>
          )}
        </p>
      )}
    </div>
  );
}

/** The small persistent indicator that the Task due date was changed (a recorded fact, never "new" or "unread"). */
export function DueChangedButton({ count, open, controls, onToggle }: { count: number; open: boolean; controls: string; onToggle: () => void }) {
  return (
    <button
      type="button"
      aria-expanded={open}
      aria-controls={controls}
      onClick={onToggle}
      data-testid="due-changed"
      title="The Task due date was changed. Open to see the previous date, the new date, the reason and who changed it."
      className="ml-1 inline-flex items-center rounded border border-amber-300 bg-amber-50 px-1.5 py-px text-[11px] font-medium leading-4 text-amber-900 hover:bg-amber-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2a7fb8]"
    >
      Due changed{count > 1 ? ` (${count})` : ''}
    </button>
  );
}

/** Each recorded change of the Task due date, newest first: previous date, new date, reason, who changed it, and the recorded time. */
export function DueChangesPanel({ id, changes, incomplete, onClose }: { id: string; changes: readonly IssuedDueChange[]; incomplete: boolean; onClose: () => void }) {
  return (
    <div id={id} role="group" aria-label="Task due date changes" data-testid="due-changes" className="mt-1 w-full rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-gray-900">
      <div className="flex items-start justify-between gap-2">
        <p className="font-semibold">Task due date changes (newest first)</p>
        <button type="button" onClick={onClose} className="rounded px-1 text-[#1f6a99] underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2a7fb8]">
          Close
        </button>
      </div>
      <ul className="mt-1 space-y-1">
        {changes.map((change, index) => (
          <li key={`${change.recordedAt}-${index}`} data-testid="due-change">
            <span className="font-medium">{change.to === '' ? `Cleared (was ${dateOf(change.from)})` : `${dateOf(change.from)} to ${dateOf(change.to)}`}</span>
            {change.reason !== '' && <span>{' - '}{change.reason}</span>}
            <span className="text-gray-600">
              {' - changed by '}
              {change.actorName !== '' ? <PersonName person={change.actorName} focusable={false} /> : 'someone'}
              {change.byRequester ? ' (requester)' : ''}
              {recordedAtText(change.recordedAt) !== '' ? `, recorded ${recordedAtText(change.recordedAt)}` : ''}
            </span>
          </li>
        ))}
      </ul>
      {incomplete && <p className="mt-1 text-gray-600">Older changes are in History.</p>}
    </div>
  );
}

/** The indicator and its panel together, for a place that has room for both under it (one recipient of an Everyone request). */
export function DueChangedInline({ changes, incomplete, idBase }: { changes: readonly IssuedDueChange[]; incomplete: boolean; idBase: string }) {
  const [open, setOpen] = useState(false);
  if (changes.length === 0) return null;
  const id = `${idBase}-changes`;
  return (
    <>
      <DueChangedButton count={changes.length} open={open} controls={id} onToggle={() => setOpen((v) => !v)} />
      {open && <DueChangesPanel id={id} changes={changes} incomplete={incomplete} onClose={() => setOpen(false)} />}
    </>
  );
}

/** What ONE recipient of an Everyone request has recorded: its own latest progress, next action and follow-up, and its own date changes. Nothing of another recipient is ever drawn here. */
export function MemberInsight({ member }: { member: IssuedMember }) {
  if (member.ended || member.restricted) return null;
  const progress = member.insight === null ? null : member.insight.progress;
  const changes = member.insight === null ? [] : member.insight.dueChanges;
  if (!hasInsightLines(progress, member.nextAction, member.followUpDate) && changes.length === 0) return null;
  return (
    <div className="basis-full space-y-1 pl-7 text-xs text-gray-700" data-testid="recipient-insight">
      <InsightLines progress={progress} nextAction={member.nextAction} followUpDate={member.followUpDate} full />
      <DueChangedInline changes={changes} incomplete={member.insight !== null && member.insight.incomplete} idBase={`due-${member.itemId}`} />
    </div>
  );
}
